import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Checkbox } from './Checkbox.tsx';
import ChevronDown from 'assets/icons/arrow-language-selector.svg?react';
import CloseIcon from 'assets/icons/close-button-small.svg?react';
import styles from './MultiSelectChips.module.scss';

export type MultiSelectOption = {
  value: string;
  label: string;
};

type OwnProps = {
  options: MultiSelectOption[];
  selected: MultiSelectOption[];
  onChange: (value: MultiSelectOption[]) => void;
  getOptionLabel: (option: MultiSelectOption) => string;
  chooseLabel: string;
  selectAllLabel: string;
  moreLabel: string;
  className?: string;
  containerTestId?: string;
  optionCheckboxTestId?: string;
  optionTextTestId?: string;
  chipsContainerClassName?: string;
  overFlowChipsContainerClassName?: string;
};

export const MultiSelectChips = ({
  options,
  selected,
  onChange,
  getOptionLabel,
  chooseLabel,
  selectAllLabel,
  moreLabel,
  className,
  containerTestId,
  optionCheckboxTestId,
  optionTextTestId,
  chipsContainerClassName,
  overFlowChipsContainerClassName,
}: OwnProps) => {
  const [isSelectVisible, setIsSelectVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const isOptionSelected = (option: MultiSelectOption) => {
    return !!selected.find(
      (selectedOption) => selectedOption.value === option.value,
    );
  };

  const isAllChecked = () => options.every(isOptionSelected);

  const removeSelectedOption = (selectedOption: MultiSelectOption) => {
    onChange(
      selected.filter((option) => option.value !== selectedOption.value),
    );
  };

  useEffect(() => {
    const closeSelect = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsSelectVisible(false);
      }
    };

    document.addEventListener('mousedown', closeSelect);

    return () => document.removeEventListener('mousedown', closeSelect);
  }, []);

  return (
    <div className={clsx(styles.relative, className)} ref={containerRef}>
      <div
        data-test-id={'select-container'}
        className={clsx(styles.selectButton, styles.spaceBetween)}
        onClick={(e) => {
          e.stopPropagation();
          setIsSelectVisible((prev) => !prev);
        }}
      >
        <div
          className={clsx(
            styles.rowContainer,
            styles.columnGap4,
            styles.minWidth0,
          )}
        >
          {!selected.length && (
            <div className={clsx(styles.greyText, styles.marginLeft10)}>
              {chooseLabel}
            </div>
          )}
          <div
            className={clsx(
              styles.rowContainer,
              styles.columnGap4,
              styles.chipsContainer,
              chipsContainerClassName,
              selected.length > 2 &&
                (overFlowChipsContainerClassName ?? styles.maxWidth264),
            )}
          >
            {selected.map((selectedOption, index) => {
              if (index > 1) {
                return null;
              }

              return (
                <div
                  key={selectedOption.value}
                  className={clsx(
                    styles.rowContainer,
                    styles.columnGap4,
                    styles.selectedType,
                  )}
                >
                  <div className={clsx(styles.nowrap, styles.chipText)}>
                    {getOptionLabel(selectedOption)}
                  </div>
                  <div className={styles.flex}>
                    <CloseIcon
                      className={styles.deleteIcon}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSelectedOption(selectedOption);
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {selected.length > 2 && (
            <div className={styles.text}>
              + {selected.length - 2} {moreLabel}
            </div>
          )}
        </div>
        <div className={styles.flex}>
          <ChevronDown />
        </div>
      </div>
      {isSelectVisible && (
        <div
          className={styles.selectContainer}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={clsx(styles.selectItem, styles.selectItemBorderBottom)}
          >
            <Checkbox
              actionText={selectAllLabel}
              isChecked={isAllChecked()}
              onChange={() => {
                if (isAllChecked()) {
                  onChange([]);
                } else {
                  onChange(options);
                }
              }}
              halfChecked={!!selected.length}
            />
          </div>
          <div className={styles.optionsContainer}>
            {options.map((option) => (
              <div
                className={clsx(
                  styles.selectItem,
                  isOptionSelected(option) && styles.selectedItem,
                )}
                key={option.value}
              >
                <Checkbox
                  testId={'select-option-checkbox'}
                  textTestId={'select-option-text'}
                  actionText={getOptionLabel(option)}
                  isChecked={isOptionSelected(option)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onChange([...selected, option]);
                    } else {
                      removeSelectedOption(option);
                    }
                  }}
                  actionTextClassName={styles.padding}
                  checkBoxClassName={styles.padding}
                  containerClassName={styles.flexStart}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
